// Per-SHA promotion state for the candidates list: which commit is shipping to
// production right now, and which ones have a request open that has not reached
// quorum yet. The hero banner already announces the in-flight promote, but it
// never says WHICH row it is, so a second promote of the same commit is easy to
// fire by accident - nothing on the server rejects a duplicate request.
//
// Kept pure and free of React so the precedence rules are unit-testable.
import type { ReleaseRequest } from './api';

export type RowPromoState = 'deploying' | 'awaiting';

export function promoStates({
  latestPromotion,
  currentProdSha,
  requests,
}: {
  /** Newest dispatched promote + its real prod deploy state (from /api/candidates). */
  latestPromotion?: { sha: string; state: string };
  currentProdSha: string | null;
  requests: ReleaseRequest[];
}): Map<string, RowPromoState> {
  const states = new Map<string, RowPromoState>();

  // Open requests still collecting approvals. Promoting again would just stack a
  // second request on the same commit.
  for (const r of requests) {
    if (r.status === 'pending') states.set(r.sha, 'awaiting');
  }

  // The deploy that is actually running. Same test the hero uses (see
  // resolveHeroState): a promote whose SHA has already become prod has landed,
  // so the row shows `current prod` instead. Set last so it wins over
  // `awaiting` when a duplicate request exists for the in-flight SHA.
  if (latestPromotion && latestPromotion.state === 'pending' && latestPromotion.sha !== currentProdSha) {
    states.set(latestPromotion.sha, 'deploying');
  }

  return states;
}

/** Which of a target's tenant options are safe to offer for a REDEPLOY (as
 * opposed to a fresh promote, which isn't restricted): a tenant is safe only
 * when its own environment's current prod already matches the code being
 * redeployed. 'polaris' deploys to `production` alone; every other tenant
 * (including 'all') also touches `feq-production` - the same base/feq split
 * the gate itself reads (see deploymentsGateStatus's `needsFeq`) - so it needs
 * BOTH environments to already agree before a "redeploy" can honestly cover it. */
export function redeployableTenants(tenants: string[], baseProdSha?: string, feqProdSha?: string): string[] {
  if (!baseProdSha) return [];
  return tenants.filter(t => t === 'polaris' || feqProdSha === baseProdSha);
}
